import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import type { RegimeMultiplierPolicy, RegimeMultiplierAsset } from '../types/api';
import { SlidersHorizontal, Plus, RefreshCw, ChevronDown, ChevronUp, Save, AlertCircle, CheckCircle, X, BarChart3 } from 'lucide-react';

const ASSET_TYPES = [
  'equity_etf', 'bond_etf', 'cash_like', 'commodity',
  'leveraged_etf', 'inverse_etf', 'volatility_etf', 'other',
];

const BULLISH_LABELS = ['TC_BULL_PERSISTENT', 'BULL_TRANSITION'];
const BEARISH_LABELS = ['TC_BEAR_PERSISTENT', 'BEAR_TRANSITION', 'REGIME_SHIFT_RISK'];
const NEUTRAL_LABELS = ['MR_BALANCED_PERSISTENT', 'LOW_CONVICTION'];

function fmt2(n: number) { return n.toFixed(2); }

interface PolicyFormState {
  policy_id: string;
  name: string;
  description: string;
  bullish_factor: string;
  neutral_factor: string;
  bearish_factor: string;
  min_multiplier: string;
  max_multiplier: string;
  normalize_weights: boolean;
  cash_fallback_symbol: string;
}

function defaultForm(): PolicyFormState {
  return {
    policy_id: '',
    name: '',
    description: '',
    bullish_factor: '1.0',
    neutral_factor: '0.8',
    bearish_factor: '0.5',
    min_multiplier: '0.0',
    max_multiplier: '1.5',
    normalize_weights: false,
    cash_fallback_symbol: 'SGOV',
  };
}

function policyToForm(p: RegimeMultiplierPolicy): PolicyFormState {
  return {
    policy_id: p.policy_id,
    name: p.name,
    description: p.description ?? '',
    bullish_factor: String(p.bullish_factor),
    neutral_factor: String(p.neutral_factor),
    bearish_factor: String(p.bearish_factor),
    min_multiplier: String(p.min_multiplier),
    max_multiplier: String(p.max_multiplier),
    normalize_weights: p.normalize_weights,
    cash_fallback_symbol: p.cash_fallback_symbol,
  };
}

function MultiplierBar({ value, min = 0, max = 1.5 }: { value: number; min?: number; max?: number }) {
  const pct = Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
  const color = value >= 1.0 ? 'bg-emerald-500' : value >= 0.7 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-200 rounded-full h-2">
        <div className={`${color} h-2 rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-mono text-gray-700 w-8 text-right">{fmt2(value)}x</span>
    </div>
  );
}

function PolicyCard({
  policy,
  onEdit,
}: {
  policy: RegimeMultiplierPolicy;
  onEdit: (p: RegimeMultiplierPolicy) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <SlidersHorizontal className="w-4 h-4 text-emerald-700" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900 text-sm">{policy.name}</span>
              {policy.is_default && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-emerald-100 text-emerald-700 uppercase">Default</span>
              )}
              {!policy.is_active && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-gray-200 text-gray-500 uppercase">Inactive</span>
              )}
            </div>
            <p className="text-xs text-gray-500 font-mono mt-0.5">{policy.policy_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => onEdit(policy)}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Edit
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
          {policy.description && (
            <p className="text-sm text-gray-600">{policy.description}</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs font-semibold text-emerald-700 mb-1.5">Bullish Labels</p>
              <div className="space-y-1.5">
                {BULLISH_LABELS.map((lbl) => (
                  <div key={lbl}>
                    <p className="text-[10px] text-gray-500 mb-0.5">{lbl}</p>
                    <MultiplierBar value={policy.bullish_factor} />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1.5">Neutral Labels</p>
              <div className="space-y-1.5">
                {NEUTRAL_LABELS.map((lbl) => (
                  <div key={lbl}>
                    <p className="text-[10px] text-gray-500 mb-0.5">{lbl}</p>
                    <MultiplierBar value={policy.neutral_factor} />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-red-600 mb-1.5">Bearish Labels</p>
              <div className="space-y-1.5">
                {BEARISH_LABELS.map((lbl) => (
                  <div key={lbl}>
                    <p className="text-[10px] text-gray-500 mb-0.5">{lbl}</p>
                    <MultiplierBar value={policy.bearish_factor} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div className="bg-gray-50 rounded-lg p-2.5">
              <p className="text-gray-500 mb-0.5">Min Multiplier</p>
              <p className="font-mono font-semibold text-gray-800">{fmt2(policy.min_multiplier)}x</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2.5">
              <p className="text-gray-500 mb-0.5">Max Multiplier</p>
              <p className="font-mono font-semibold text-gray-800">{fmt2(policy.max_multiplier)}x</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2.5">
              <p className="text-gray-500 mb-0.5">Freed Weight Goes To</p>
              <p className="font-mono font-semibold text-gray-800">
                {policy.normalize_weights ? 'Normalize (redistribute)' : `${policy.cash_fallback_symbol} (cash)`}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2.5">
              <p className="text-gray-500 mb-0.5">Version</p>
              <p className="font-mono font-semibold text-gray-800">v{policy.version ?? 1}</p>
            </div>
          </div>
          {Object.keys(policy.symbol_overrides).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1.5">Symbol Overrides</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(policy.symbol_overrides).map(([sym, factor]) => (
                  <span key={sym} className="px-2 py-1 text-xs font-mono rounded bg-amber-50 border border-amber-200 text-amber-800">
                    {sym}: {String(factor)}x
                  </span>
                ))}
              </div>
            </div>
          )}
          {Object.keys(policy.asset_type_overrides).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1.5">Asset Type Overrides</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(policy.asset_type_overrides).map(([type, factor]) => (
                  <span key={type} className="px-2 py-1 text-xs font-mono rounded bg-blue-50 border border-blue-200 text-blue-800">
                    {type}: {String(factor)}x
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface OverrideEntry { key: string; value: string; }

// Asset type overrides can be either a scalar factor (number) or a per-regime
// object { bullish_factor, neutral_factor, bearish_factor }.  We track both
// shapes in state so the editor never corrupts structured overrides.
interface AssetOverrideEntry {
  key: string;
  isStructured: boolean;
  scalar: string;    // used when isStructured=false
  bullish: string;   // used when isStructured=true
  neutral: string;   // used when isStructured=true
  bearish: string;   // used when isStructured=true
}

function _loadAssetOverrides(raw: Record<string, unknown>): AssetOverrideEntry[] {
  return Object.entries(raw).map(([k, v]) => {
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      const obj = v as Record<string, unknown>;
      return {
        key: k,
        isStructured: true,
        scalar: '1.0',
        bullish: String(obj.bullish_factor ?? 1.0),
        neutral: String(obj.neutral_factor ?? 1.0),
        bearish: String(obj.bearish_factor ?? 1.0),
      };
    }
    return { key: k, isStructured: false, scalar: String(v ?? 1.0), bullish: '1.0', neutral: '1.0', bearish: '1.0' };
  });
}

function PolicyForm({
  editingPolicy,
  onClose,
  onSaved,
}: {
  editingPolicy: RegimeMultiplierPolicy | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isCreate = editingPolicy === null;
  const [form, setForm] = useState<PolicyFormState>(
    editingPolicy ? policyToForm(editingPolicy) : defaultForm()
  );
  const [symbolOverrides, setSymbolOverrides] = useState<OverrideEntry[]>(
    Object.entries(editingPolicy?.symbol_overrides ?? {}).map(([k, v]) => ({ key: k, value: String(v) }))
  );
  const [assetOverrides, setAssetOverrides] = useState<AssetOverrideEntry[]>(
    _loadAssetOverrides((editingPolicy?.asset_type_overrides as Record<string, unknown>) ?? {})
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const setField = (k: keyof PolicyFormState, v: string | boolean) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const symOver = Object.fromEntries(
        symbolOverrides.filter((e) => e.key).map((e) => [e.key.toUpperCase(), parseFloat(e.value) || 1.0])
      );
      const assetOver = Object.fromEntries(
        assetOverrides.filter((e) => e.key).map((e) => [
          e.key,
          e.isStructured
            ? { bullish_factor: parseFloat(e.bullish) || 1.0, neutral_factor: parseFloat(e.neutral) || 1.0, bearish_factor: parseFloat(e.bearish) || 1.0 }
            : (parseFloat(e.scalar) || 1.0),
        ])
      );
      const payload = {
        name: form.name,
        description: form.description || undefined,
        bullish_factor: parseFloat(form.bullish_factor) || 1.0,
        neutral_factor: parseFloat(form.neutral_factor) || 0.8,
        bearish_factor: parseFloat(form.bearish_factor) || 0.5,
        min_multiplier: parseFloat(form.min_multiplier) || 0.0,
        max_multiplier: parseFloat(form.max_multiplier) || 1.5,
        normalize_weights: form.normalize_weights,
        cash_fallback_symbol: form.cash_fallback_symbol || 'SGOV',
        symbol_overrides: symOver,
        asset_type_overrides: assetOver,
      };
      if (isCreate) {
        await api.createRegimeMultiplierPolicy({ policy_id: form.policy_id, ...payload });
      } else {
        await api.updateRegimeMultiplierPolicy(editingPolicy!.policy_id, payload);
      }
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const numberField = (label: string, key: keyof PolicyFormState, help?: string) => (
    <div>
      <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
      {help && <p className="text-xs text-gray-400 mb-1">{help}</p>}
      <input
        type="number"
        step="0.01"
        min="0"
        max="10"
        value={form[key] as string}
        onChange={(e) => setField(key, e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4 bg-gray-900/40 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mb-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">
            {isCreate ? 'Create Policy' : `Edit: ${editingPolicy!.name}`}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {isCreate && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Policy ID <span className="text-red-500">*</span></label>
              <input
                type="text"
                placeholder="e.g. conservative"
                value={form.policy_id}
                onChange={(e) => setField('policy_id', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          <div>
            <p className="text-xs font-bold text-gray-800 mb-3 uppercase tracking-wide">Regime Factors</p>
            <div className="grid grid-cols-3 gap-4">
              {numberField('Bullish Factor', 'bullish_factor', 'TC_BULL_*, BULL_TRANSITION')}
              {numberField('Neutral Factor', 'neutral_factor', 'MR_BALANCED_*, LOW_CONVICTION')}
              {numberField('Bearish Factor', 'bearish_factor', 'TC_BEAR_*, REGIME_SHIFT_RISK')}
            </div>
          </div>

          <div>
            <p className="text-xs font-bold text-gray-800 mb-3 uppercase tracking-wide">Clamp Range</p>
            <div className="grid grid-cols-2 gap-4">
              {numberField('Min Multiplier', 'min_multiplier')}
              {numberField('Max Multiplier', 'max_multiplier')}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Cash Fallback Symbol</label>
              <p className="text-xs text-gray-400 mb-1">Freed allocation redirected here when normalize_weights=off</p>
              <input
                type="text"
                value={form.cash_fallback_symbol}
                onChange={(e) => setField('cash_fallback_symbol', e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div className="flex flex-col justify-end">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.normalize_weights}
                  onChange={(e) => setField('normalize_weights', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <div>
                  <p className="text-xs font-semibold text-gray-700">Normalize Weights</p>
                  <p className="text-xs text-gray-400">Redistributes freed weight proportionally. Off = cash drag via fallback symbol.</p>
                </div>
              </label>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-gray-800 uppercase tracking-wide">Symbol Overrides</p>
              <button
                type="button"
                onClick={() => setSymbolOverrides((v) => [...v, { key: '', value: '1.0' }])}
                className="text-xs text-emerald-700 hover:text-emerald-800 font-semibold"
              >
                + Add
              </button>
            </div>
            <p className="text-xs text-gray-400 mb-2">Override the computed multiplier for specific tickers (e.g. TQQQ: 0.5)</p>
            {symbolOverrides.map((entry, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  type="text"
                  placeholder="TICKER"
                  value={entry.key}
                  onChange={(e) => setSymbolOverrides((v) => v.map((x, j) => j === i ? { ...x, key: e.target.value.toUpperCase() } : x))}
                  className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-emerald-500"
                />
                <input
                  type="number"
                  step="0.05"
                  min="0"
                  placeholder="1.0"
                  value={entry.value}
                  onChange={(e) => setSymbolOverrides((v) => v.map((x, j) => j === i ? { ...x, value: e.target.value } : x))}
                  className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setSymbolOverrides((v) => v.filter((_, j) => j !== i))}
                  className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-gray-800 uppercase tracking-wide">Asset Type Overrides</p>
              <button
                type="button"
                onClick={() => setAssetOverrides((v) => [...v, { key: ASSET_TYPES[0], isStructured: false, scalar: '1.0', bullish: '1.0', neutral: '1.0', bearish: '1.0' }])}
                className="text-xs text-emerald-700 hover:text-emerald-800 font-semibold"
              >
                + Add
              </button>
            </div>
            <p className="text-xs text-gray-400 mb-2">
              Override by asset type — either a single scalar (applied in all regimes) or per-regime values (bull/neutral/bear).
            </p>
            {assetOverrides.map((entry, i) => (
              <div key={i} className="mb-3 p-3 border border-gray-200 rounded-lg bg-gray-50 space-y-2">
                <div className="flex items-center gap-2">
                  <select
                    value={entry.key}
                    onChange={(e) => setAssetOverrides((v) => v.map((x, j) => j === i ? { ...x, key: e.target.value } : x))}
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 bg-white"
                  >
                    {ASSET_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={entry.isStructured}
                      onChange={(e) => setAssetOverrides((v) => v.map((x, j) => j === i ? { ...x, isStructured: e.target.checked } : x))}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-600"
                    />
                    Per-regime
                  </label>
                  <button
                    type="button"
                    onClick={() => setAssetOverrides((v) => v.filter((_, j) => j !== i))}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {entry.isStructured ? (
                  <div className="grid grid-cols-3 gap-2">
                    {(
                      [['bullish', 'Bullish ×', 'text-emerald-700'], ['neutral', 'Neutral ×', 'text-gray-600'], ['bearish', 'Bearish ×', 'text-red-600']] as const
                    ).map(([regime, label, cls]) => (
                      <div key={regime}>
                        <label className={`block text-[10px] font-semibold mb-0.5 ${cls}`}>{label}</label>
                        <input
                          type="number"
                          step="0.05"
                          min="0"
                          value={entry[regime]}
                          onChange={(e) => setAssetOverrides((v) => v.map((x, j) => j === i ? { ...x, [regime]: e.target.value } : x))}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm font-mono focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-500 mb-0.5">Scalar (all regimes) ×</label>
                    <input
                      type="number"
                      step="0.05"
                      min="0"
                      value={entry.scalar}
                      onChange={(e) => setAssetOverrides((v) => v.map((x, j) => j === i ? { ...x, scalar: e.target.value } : x))}
                      className="w-24 px-3 py-1 border border-gray-300 rounded text-sm font-mono focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {saveError && (
            <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {saveError}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.name || (isCreate && !form.policy_id)}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save Policy'}
          </button>
        </div>
      </div>
    </div>
  );
}

const ASSET_TYPE_ORDER = [
  'equity_etf', 'leveraged_etf', 'inverse_etf', 'volatility_etf',
  'bond_etf', 'cash_like', 'commodity', 'other',
];

const ASSET_TYPE_LABELS: Record<string, string> = {
  equity_etf: 'Equity ETF',
  leveraged_etf: 'Leveraged ETF',
  inverse_etf: 'Inverse ETF',
  volatility_etf: 'Volatility ETF',
  bond_etf: 'Bond ETF',
  cash_like: 'Cash / Short-Term',
  commodity: 'Commodity',
  other: 'Other',
};

function multColor(v: number | null): string {
  if (v === null) return 'text-gray-400';
  if (v >= 1.0) return 'text-emerald-700 font-semibold';
  if (v >= 0.7) return 'text-amber-600 font-semibold';
  return 'text-red-600 font-semibold';
}

function probBar(v: number | undefined) {
  if (v == null) return <span className="text-gray-300 text-xs">—</span>;
  const pct = Math.round(v * 100);
  return <span className="font-mono text-xs">{pct}%</span>;
}

export function RegimeMultiplierAdmin() {
  const [policies, setPolicies] = useState<RegimeMultiplierPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<RegimeMultiplierPolicy | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  const [assetPolicyId, setAssetPolicyId] = useState<string>('');
  const [assets, setAssets] = useState<RegimeMultiplierAsset[]>([]);
  const [assetAsOfDate, setAssetAsOfDate] = useState<string>('');
  const [assetLoading, setAssetLoading] = useState(false);
  const [assetError, setAssetError] = useState<string | null>(null);

  const loadPolicies = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getRegimeMultiplierPolicies();
      setPolicies(data.policies ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load policies.');
    } finally {
      setLoading(false);
    }
  };

  const loadAssets = async (policyId?: string) => {
    setAssetLoading(true);
    setAssetError(null);
    try {
      const data = await api.getRegimeMultiplierAssets(policyId || undefined);
      setAssets(data.assets ?? []);
      setAssetAsOfDate(data.as_of_date ?? '');
    } catch (err) {
      setAssetError(err instanceof Error ? err.message : 'Failed to load asset preview.');
    } finally {
      setAssetLoading(false);
    }
  };

  useEffect(() => { loadPolicies(); }, []);

  const handleEdit = (p: RegimeMultiplierPolicy) => {
    setEditingPolicy(p);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingPolicy(null);
    setShowForm(true);
  };

  const handleSaved = () => {
    setShowForm(false);
    setSavedOk(true);
    setTimeout(() => setSavedOk(false), 3000);
    loadPolicies();
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Regime Position Multipliers</h1>
          <p className="text-gray-600 mt-1 text-base">
            Manage policies that scale position sizes based on detected market regimes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadPolicies}
            disabled={loading}
            className="p-2 rounded-lg border border-gray-300 text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            New Policy
          </button>
        </div>
      </div>

      {savedOk && (
        <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm font-medium">
          <CheckCircle className="w-4 h-4" />
          Policy saved successfully.
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
        <p className="font-semibold mb-1">How it works</p>
        <p>
          Each policy defines multipliers for bullish, neutral, and bearish regime labels. When a strategy runs with
          a multiplier enabled, each asset's weight is scaled by its effective multiplier.
          With <strong>normalize_weights = off</strong> (recommended), any freed allocation flows to the cash fallback
          symbol (SGOV by default), preserving cash drag. With normalize on, all remaining weights are rescaled to sum to 100%.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-6 h-6 animate-spin text-emerald-600" />
        </div>
      ) : policies.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <SlidersHorizontal className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="font-medium">No policies yet.</p>
          <p className="text-sm mt-1">Create a policy to start using regime-aware position sizing.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {policies.map((p) => (
            <PolicyCard key={p.policy_id} policy={p} onEdit={handleEdit} />
          ))}
        </div>
      )}

      {/* ── Asset Multiplier Preview ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Asset Multiplier Preview</h2>
              {assetAsOfDate && (
                <p className="text-xs text-gray-400 mt-0.5">As of {assetAsOfDate}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={assetPolicyId}
              onChange={(e) => setAssetPolicyId(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Default policy</option>
              {policies.map((p) => (
                <option key={p.policy_id} value={p.policy_id}>
                  {p.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => loadAssets(assetPolicyId || undefined)}
              disabled={assetLoading}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {assetLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Load
            </button>
          </div>
        </div>

        {assetError && (
          <div className="flex items-center gap-2 text-red-700 bg-red-50 border-b border-red-100 px-5 py-3 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {assetError}
          </div>
        )}

        {assets.length === 0 && !assetLoading && !assetError && (
          <div className="text-center py-10 text-gray-400 text-sm">
            Select a policy and click <strong>Load</strong> to preview effective multipliers.
          </div>
        )}

        {assetLoading && (
          <div className="flex items-center justify-center py-10">
            <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
          </div>
        )}

        {assets.length > 0 && (() => {
          const grouped: Record<string, RegimeMultiplierAsset[]> = {};
          for (const a of assets) {
            const t = a.asset_type ?? 'other';
            if (!grouped[t]) grouped[t] = [];
            grouped[t].push(a);
          }
          const types = ASSET_TYPE_ORDER.filter((t) => grouped[t]?.length);
          // Add any types not in our order list
          for (const t of Object.keys(grouped)) {
            if (!ASSET_TYPE_ORDER.includes(t)) types.push(t);
          }
          return (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Symbol</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Asset Type</th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Has Score</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">As Of Date</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Regime Label</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Base Position Multiplier</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Effective Multiplier</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Bull Probability</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Bear Probability</th>
                  </tr>
                </thead>
                <tbody>
                  {types.map((type) => (
                    <>
                      <tr key={`hdr-${type}`} className="bg-gray-50/60 border-t border-gray-100">
                        <td colSpan={9} className="px-4 py-1.5">
                          <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                            {ASSET_TYPE_LABELS[type] ?? type}
                          </span>
                        </td>
                      </tr>
                      {grouped[type].map((a) => (
                        <tr key={a.symbol} className="border-t border-gray-100 hover:bg-gray-50/50 transition-colors">
                          <td className="px-4 py-2 font-mono font-semibold text-gray-800">{a.symbol}</td>
                          <td className="px-4 py-2 text-xs text-gray-500">{ASSET_TYPE_LABELS[a.asset_type ?? 'other'] ?? a.asset_type}</td>
                          <td className="px-4 py-2 text-center">
                            {a.has_score
                              ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mx-auto" />
                              : <span className="w-3.5 h-3.5 rounded-full bg-gray-200 inline-block" />}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-500 font-mono">{a.as_of_date || '—'}</td>
                          <td className="px-4 py-2 text-xs">
                            {a.regime_label
                              ? <span className="px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700 text-[10px] font-mono">{a.regime_label}</span>
                              : <span className="text-gray-300">—</span>}
                          </td>
                          <td className={`px-4 py-2 text-right font-mono text-xs ${multColor(a.base_multiplier)}`}>
                            {a.base_multiplier != null ? `${a.base_multiplier.toFixed(2)}×` : '—'}
                          </td>
                          <td className={`px-4 py-2 text-right font-mono text-xs ${multColor(a.effective_multiplier)}`}>
                            {a.effective_multiplier != null ? `${a.effective_multiplier.toFixed(2)}×` : '—'}
                          </td>
                          <td className="px-4 py-2 text-right">{probBar(a.bull_prob)}</td>
                          <td className="px-4 py-2 text-right">{probBar(a.bear_prob)}</td>
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {showForm && (
        <PolicyForm
          editingPolicy={editingPolicy}
          onClose={() => setShowForm(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
